import React, { useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import ReduxStatusVisualizedComponent from '../../../../components/ReduxStatusVisualizedComponent';
import {
  BACKGROUND_GREY,
  BACKGROUND_BLACK,
  BLACK,
  ORANGE,
  WHITE,
  TEXT_GREY,
} from '../../../../styles/Colors';
import { HEADER_HIGHT } from '../../../../styles/LayoutSetting';
import { mobileDevice } from '../../../../constants/DeviceBreakPoints';

const useStyles = makeStyles(theme => ({
  screenContainer: {
    backgroundColor: BACKGROUND_GREY,
    paddingTop: HEADER_HIGHT,
    width: '100vw',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  wrapperContainer: {
    width: '100%',
    maxWidth: 1000,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    minHeight: `calc((100vh - ${HEADER_HIGHT}px)*0.8)`,
    padding: '30px 0',
    [theme.breakpoints.down(mobileDevice)]: {
      height: 'auto',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'center',
      padding: 0,
    },
  },
  formContainer: {
    width: '45%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '0 0 30px 0',
    [theme.breakpoints.down(mobileDevice)]: {
      width: '100%',
    },
  },
  formStyle: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0 10px',
    '& .MuiTextField-root': {
      margin: theme.spacing(1),
      width: 300,
      maxWidth: '90%',
    },
  },
  titleText: {
    fontFamily: 'Roboto',
    color: ORANGE,
    fontSize: 24,
    fontWeight: 'bold',
    textAlign: 'left',
    lineHeight: 1.2,
    margin: 10,
    [theme.breakpoints.down(mobileDevice)]: {
      fontSize: 18,
      textAlign: 'center',
    },
  },
  textContainer: {
    width: '55%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    [theme.breakpoints.down(mobileDevice)]: {
      width: 'auto',
      margin: '30px 0',
    },
  },
  descriptionContainer: {
    width: '80%',
    margin: 10,
  },
  descriptionText: {
    fontFamily: 'Quicksand, sans-serif',
    color: TEXT_GREY,
    fontSize: 18,
    textAlign: 'left',
    [theme.breakpoints.down(mobileDevice)]: {
      fontSize: 16,
    },
  },
  highlightText: {
    color: BACKGROUND_BLACK,
    fontFamily: 'Roboto',
    fontWeight: 'bold',
  },
  navItem: {
    color: BLACK,
    fontSize: 18,
    fontWeight: 'bold',
    textDecoration: 'none',
    borderRadius: 10,
    padding: '8px 20px',
    textAlign: 'center',
    backgroundColor: ORANGE,
    '&:hover': {
      color: WHITE,
    },
    marginTop: 30,
    [theme.breakpoints.down(mobileDevice)]: {
      fontSize: 16,
    },
  },
  buttonStyle: {
    width: 140,
    borderRadius: 40,
    textTransform: 'none',
    color: BLACK,
    margin: '10px 8px 0px 8px',
    backgroundColor: ORANGE,
    '&:hover': {
      color: WHITE,
    },
    [theme.breakpoints.down(mobileDevice)]: {
      fontSize: 16,
    },
  },
}));

const ContactInfo = [
  {
    fieldKey: 'firstName',
    label: 'First Name',
    inputType: 'name',
  }, {
    fieldKey: 'lastName',
    label: 'Last Name',
    inputType: 'name',
  }, {
    fieldKey: 'email',
    label: 'Work Email',
    inputType: 'email',
  }, {
    fieldKey: 'mobilePhone',
    label: 'Mobile Phone',
    inputType: 'phone',
  }, {
    fieldKey: 'company',
    label: 'Company Name',
    inputType: 'text',
  },
];
const formDataInitial = {
  type: 'TryRiskFree',
  firstName: '',
  lastName: '',
  email: '',
  mobilePhone: '',
  company: '',
};

const TryRiskFreeScreen = ({
  reduxStatus,
  reduxError,
  onSendQuestion,
}) => {
  const classes = useStyles();
  const [formData, setFormData] = useState(formDataInitial);
  const onFormDataChange = (event, fieldKey) => {
    const newValue = R.pathOr('', ['target', 'value'], event);
    const newFormData = {
      ...formData,
      [fieldKey]: newValue,
    };
    setFormData(newFormData);
  };
  const handleSubmitForm = (event) => {
    event.preventDefault();
    const invalidContent = R.find(content => content === '', formData);
    if (R.isNil(invalidContent)) {
      onSendQuestion(formData);
    }
  };

  const FormContent = (
    <div className={classes.formContainer}>
      <form
        className={classes.formStyle}
        onSubmit={handleSubmitForm}
      >
        {ContactInfo.map(({
          fieldKey,
          label,
          inputType,
        }) => (
          <TextField
            id={fieldKey}
            key={fieldKey}
            value={formData.fieldKey}
            onChange={(event) => { onFormDataChange(event, fieldKey); }}
            type={inputType}
            label={label}
            required
            variant="filled"
          />
        ))}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.buttonStyle}
        >
          Submit
        </Button>
      </form>
    </div>
  );
  const SayingContent = (
    <div className={classes.textContainer}>
      <div className={classes.titleText}>
        Try LinkedField, Risk Free
      </div>
      <div className={classes.descriptionContainer}>
        <span className={`${classes.descriptionText} ${classes.highlightText}`}>
          {[
            'We’ll send you all equipment needed and set you up, for free.',
          ].join(' ')}
        </span>
      </div>
      <div className={classes.descriptionContainer}>
        <span className={classes.descriptionText}>
          {[
            'LinkedField requires the use of an iPad, so you can',
            'either buy one on your own or we can loan you one of ours.',
            '',
          ].join(' ')}
          <span className={`${classes.descriptionText} ${classes.highlightText}`}>
            {[
              'You will only be charged if you don’t return our iPad.',
            ].join(' ')}
          </span>
        </span>
      </div>
      <div className={classes.descriptionContainer}>
        <span className={classes.descriptionText}>
          {[
            'Please fill out the form so we can understand some information about you',
            'and let you know if you are eligible.',
          ].join(' ')}
        </span>
      </div>
    </div>
  );
  return (
    <div className={classes.screenContainer}>
      <div className={classes.wrapperContainer}>
        {SayingContent}
        {FormContent}
      </div>
      <ReduxStatusVisualizedComponent
        reduxStatus={reduxStatus}
        error={reduxError}
      />
    </div>
  );
};

TryRiskFreeScreen.propTypes = {
  reduxStatus: PropTypes.string,
  reduxError: PropTypes.string,
  onSendQuestion: PropTypes.func,
};

TryRiskFreeScreen.defaultProps = {
  reduxStatus: null,
  reduxError: null,
  onSendQuestion: () => {},
};

export default TryRiskFreeScreen;
